import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, X, AlertTriangle, Keyboard, Send } from "lucide-react";

/**
 * iPad + mobile camera barcode/QR scanner.
 * Calls onScan(decodedText) when a code is read. Auto-stops on first scan.
 *
 * Two decode paths, in priority order:
 *  1. Native `BarcodeDetector` API — iOS 17+ Safari, modern Chrome. Vastly
 *     faster + more forgiving than the JS decoder because the OS/browser
 *     does the work. When available we bypass html5-qrcode entirely and
 *     drive our own `getUserMedia` + `requestAnimationFrame` loop.
 *  2. Fallback to `html5-qrcode` for older browsers.
 *
 * Also renders a manual-entry field so scanning is never a hard blocker —
 * if the camera can't decode the label (e.g. glare, faded print), the
 * user can type the SKU or scan number and hit Enter.
 */

const hasNativeDetector = () =>
  typeof window !== "undefined" && typeof window.BarcodeDetector === "function";

const QrScanDialog = ({ open, onClose, onScan }) => {
  const [err, setErr] = useState(null);
  const [running, setRunning] = useState(false);
  const [mode, setMode] = useState("native"); // "native" | "html5" — set at mount
  const [manual, setManual] = useState("");
  // Human-readable status line for the "black screen" debug case — the
  // user needs to know whether the camera is loading, playing, or
  // silently failed. Persisted independently of `err` so a transient
  // stream stall doesn't hide the manual-entry input.
  const [status, setStatus] = useState("Initialising…");
  // Bump this to force the effect to re-run when the user taps Retry.
  const [attempt, setAttempt] = useState(0);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const html5Ref = useRef(null);
  const scannedRef = useRef(false);

  const finish = (text) => {
    if (scannedRef.current) return;
    scannedRef.current = true;
    stopAll();
    onScan?.(text);
  };

  const stopAll = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => {
        try { t.stop(); } catch (_) { /* ignore */ }
      });
      streamRef.current = null;
    }
    const h = html5Ref.current;
    if (h) {
      try {
        const p = h.stop();
        if (p && typeof p.then === "function") {
          p.then(() => { try { h.clear(); } catch (_) { /* ignore */ } })
           .catch(() => { /* already stopped */ });
        }
      } catch (_) { /* stop() before start() completes */ }
      html5Ref.current = null;
    }
    setRunning(false);
  };

  useEffect(() => {
    if (!open) return;
    setErr(null);
    scannedRef.current = false;

    const startNative = async () => {
      try {
        // Wait one frame so React has definitely mounted <video ref>.
        // Without this, videoRef.current can be null on very fast opens.
        await new Promise((r) => setTimeout(r, 0));
        const video = videoRef.current;
        if (!video) {
          setMode("html5");
          return;
        }
        // Explicit attributes BEFORE setting srcObject — iOS Safari
        // silently refuses to play inline unless both attribute variants
        // are present on the element itself.
        video.setAttribute("playsinline", "true");
        video.setAttribute("webkit-playsinline", "true");
        video.setAttribute("autoplay", "true");
        video.muted = true;
        // Rear camera on mobile; fall through to "any" if constraints reject.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        streamRef.current = stream;
        video.srcObject = stream;
        try {
          await video.play();
        } catch (playErr) {
          // Some iOS versions require calling play() again after a tick
          // once the metadata is loaded. Retry once, silently.
          await new Promise((r) => {
            const onReady = () => {
              video.removeEventListener("loadedmetadata", onReady);
              r();
            };
            video.addEventListener("loadedmetadata", onReady);
            setTimeout(r, 800);
          });
          try { await video.play(); } catch (_) { /* keep going — the frames may still tick */ }
        }
        setRunning(true);
        const detector = new BarcodeDetector({
          formats: ["qr_code", "code_128", "ean_13", "ean_8", "code_39"],
        });
        const tick = async () => {
          if (scannedRef.current || !video || video.readyState < 2) {
            if (!scannedRef.current) rafRef.current = requestAnimationFrame(tick);
            return;
          }
          try {
            const codes = await detector.detect(video);
            if (codes && codes.length > 0) {
              const raw = (codes[0].rawValue || "").trim();
              if (raw) return finish(raw);
            }
          } catch (_) { /* transient decode error — try next frame */ }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        // Diagnose the failure so the user knows why the preview is black
        // instead of just seeing an empty rectangle.
        const name = e?.name || "";
        const msg = e?.message || String(e);
        if (name === "NotAllowedError" || /denied/i.test(msg)) {
          setErr("Camera permission denied. Enable camera access in browser settings, or use manual entry below.");
        } else if (name === "NotFoundError") {
          setErr("No camera found on this device.");
        } else if (name === "NotReadableError") {
          setErr("Camera is in use by another app. Close other apps using the camera and tap Retry.");
        } else if (name === "OverconstrainedError") {
          setErr("Camera couldn't run at requested settings. Retrying with fallback.");
        } else {
          setErr(`Camera error: ${name || "unknown"} — ${msg}`);
        }
        setStatus("Camera failed — see error below or use manual entry");
        setMode("html5");
      }
    };

    const startHtml5 = async () => {
      const elId = "qr-scan-region";
      const scanner = new Html5Qrcode(elId);
      html5Ref.current = scanner;
      try {
        setStatus("Fallback decoder starting…");
        await scanner.start(
          { facingMode: "environment" },
          { fps: 15 },
          (decoded) => finish(decoded),
          () => { /* per-frame parse failure — silent */ },
        );
        setRunning(true);
        setStatus("Camera live (fallback) — point at the code");
      } catch (e) {
        setErr(e?.message || "Could not access camera");
        setStatus("Fallback failed too — use manual entry below");
      }
    };

    if (mode === "native" && hasNativeDetector()) {
      startNative();
    } else {
      // Explicitly reflect that we've fallen back so the debug footer
      // doesn't lie about which decoder is running.
      if (mode !== "html5") setMode("html5");
      else startHtml5();
    }

    return stopAll;
  }, [open, mode, attempt]);

  if (!open) return null;

  const submitManual = () => {
    const v = manual.trim();
    if (!v) return;
    finish(v);
  };

  return (
    <div
      className="fixed inset-0 bg-black/85 z-[55] flex items-center justify-center p-4"
      data-testid="qr-scan-overlay"
    >
      <div className="nb-card-dark p-5 w-full max-w-lg" data-testid="qr-scan-dialog">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="label-eyebrow text-white/60">Scan</div>
            <h3 className="font-display text-2xl text-white mt-1 flex items-center gap-2">
              <Camera size={22} strokeWidth={2.5} /> POINT AT BARCODE / QR
            </h3>
          </div>
          <button
            data-testid="qr-scan-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
          >
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        <div className="bg-black rounded-xl overflow-hidden border-2 border-white/30 relative aspect-square sm:aspect-video">
          {/* Both containers are always mounted so switching decoder path
              (native ↔ html5) never races the re-render — one stays
              hidden depending on which mode is active. */}
          <video
            ref={videoRef}
            className={`w-full h-full object-cover ${mode === "native" ? "" : "hidden"}`}
            data-testid="qr-scan-video"
            autoPlay
            muted
            playsInline
          />
          <div
            id="qr-scan-region"
            className={`w-full h-full ${mode === "html5" ? "" : "hidden"}`}
          />
          {!running && !err && (
            <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
              Starting camera…
            </div>
          )}
        </div>

        {err ? (
          <div className="mt-3 flex items-start gap-2 text-[#FF8A95] text-sm bg-red-950/30 border border-red-400/30 rounded-lg p-2">
            <AlertTriangle size={16} strokeWidth={2.5} className="mt-0.5 flex-shrink-0" /> <span>{err}</span>
          </div>
        ) : (
          <div className="text-white/70 text-xs mt-3">
            Fill the frame with the QR code and hold steady. If the preview
            stays black or the code doesn&apos;t pick up, type the SKU below.
          </div>
        )}

        {/* Live camera status — helps diagnose the black preview case. */}
        <div
          className="mt-2 text-[11px] text-white/60 flex items-center justify-between gap-2"
          data-testid="qr-scan-status"
        >
          <span>
            <span className="inline-block w-2 h-2 rounded-full mr-1.5"
              style={{ background: running ? "#4CAF50" : err ? "#E5263F" : "#FFC107" }}
            />
            {status}
          </span>
          <button
            type="button"
            data-testid="qr-scan-retry"
            onClick={() => {
              setErr(null);
              setStatus("Retrying…");
              setMode("native");
              setAttempt((n) => n + 1);
            }}
            className="text-white/80 underline hover:text-white"
          >
            Retry
          </button>
        </div>

        {/* Manual entry fallback — always visible so scanning is never a
            hard blocker. Also useful when the label is faded or cracked
            or when the camera preview stays black (iOS permission quirks). */}
        <div className="mt-3">
          <div className="label-eyebrow text-white/60 mb-1">Or type it in</div>
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 bg-white rounded-lg border-2 border-white/40 px-3 py-2.5">
              <Keyboard size={16} strokeWidth={2.5} className="text-[#607087]" />
              <input
                data-testid="qr-scan-manual"
                className="flex-1 bg-transparent outline-none text-sm"
                placeholder="Type SKU or paste value…"
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitManual();
                }}
                autoComplete="off"
                autoCapitalize="characters"
              />
            </div>
            <button
              data-testid="qr-scan-manual-submit"
              onClick={submitManual}
              disabled={!manual.trim()}
              className="nb-btn nb-btn-primary px-4 py-2.5 flex items-center gap-1 text-sm"
            >
              <Send size={14} strokeWidth={2.5} /> Go
            </button>
          </div>
        </div>

        {/* Tiny footer showing which decoder path we're on, useful for
            debugging when the user reports scanning issues. */}
        <div className="text-[10px] text-white/40 mt-2 tracking-wider uppercase">
          Decoder: {mode === "native" ? "native camera API" : "javascript fallback"}
        </div>
      </div>
    </div>
  );
};

export default QrScanDialog;
